import { ref } from "vue";
import { defineStore } from "pinia";
import { useBapi } from "@/bapi-client";
import { BAPI_COMMANDS } from "@/bapi-client/types/commands";
import { InvoicingPayee, InvoicingConstants } from "@/bapi-client/types/external-invoicing";
import { usePaged } from "@/composables/paged";
import { GetAllInvoicesParams } from "@/bapi-client/types/external-invoicing";
import type { InvoiceFilters } from "@/types/invoices";
import { toast } from "vue-sonner";
import useTrackerStore from "@/stores/trackers";
import { RUDDERSTACK_EVENTS } from "@/lib/rudderstack";

export const useInvoicingStore = defineStore("invoicing", () => {
  const selectedPayee = ref<InvoicingPayee>();
  const { total, page, sort } = usePaged(1, 25, "ASC", "waybill_date");
  const constantList = ref<InvoicingConstants>();

  const trackerStore = useTrackerStore();

  const filterDefaults: InvoiceFilters = {
    asset_ids: "",
    status: ["Received", "Draft"],
    source_410: true,
    source_telegraph: true,
    alert_types: undefined,
    invoice_start_date: undefined,
    invoice_end_date: undefined,
    waybill_start_date: undefined,
    waybill_end_date: undefined,
    payers: [],
  };
  const filters = ref<InvoiceFilters>({
    ...filterDefaults,
  });

  async function getPayee(customerId: string) {
    const response = await useBapi(BAPI_COMMANDS.EXTERNAL_INVOICING_GET_PAYEE, customerId);
    if (response.success) {
      selectedPayee.value = response.data;
    }
    return response;
  }

  async function getInvoice(customerId: string, invoiceId: string) {
    if (!selectedPayee.value) {
      // get the payee first
      const response = await getPayee(customerId);
      if (!response.success) return response;
      selectedPayee.value = response.data;
    }
    const result = await useBapi(
      BAPI_COMMANDS.EXTERNAL_INVOICING_GET_INVOICE,
      customerId,
      selectedPayee.value.id,
      invoiceId,
    );
    if (!result.success) return result;
    return result;
  }

  async function getInvoicingConstants(customerId: string) {
    const result = await useBapi(BAPI_COMMANDS.EXTERNAL_INVOICING_GET_CONSTANTS, customerId);
    if (!result.success) {
      return result;
    }
    constantList.value = result.data;
  }

  function getFilterParams() {
    const params: GetAllInvoicesParams = {
      page: page.current.toString(),
      page_size: page.size.toString(),
      sort_column: sort.column,
      sort_direction: sort.direction,
    };
    if (filters.value.asset_ids.length) {
      params.asset_ids = filters.value.asset_ids.replace(/\n/g, ",").replace(/\s+/g, "").toUpperCase();
    }
    params.status = filters.value.status.join(",").toLocaleUpperCase();
    if (filters.value.status.includes("SENT")) {
      params.status += ",SENT_210_REJECTED";
    }
    params.source =
      filters.value.source_410 && filters.value.source_telegraph
        ? "410,MANUAL"
        : filters.value.source_410
          ? "410"
          : "MANUAL";
    params.payers = filters.value.payers?.join(",");
    if (filters.value.invoice_start_date && filters.value.invoice_end_date) {
      params.invoice_start_date = filters.value.invoice_start_date;
      params.invoice_end_date = filters.value.invoice_end_date;
    }
    if (filters.value.waybill_start_date && filters.value.waybill_end_date) {
      params.waybill_start_date = filters.value.waybill_start_date;
      params.waybill_end_date = filters.value.waybill_end_date;
    }
    if (filters.value.alert_types?.length) {
      params.alert_types = filters.value.alert_types.join(",");
    }

    return params;
  }

  function resetFilters() {
    trackerStore.logRudderstackEvent(RUDDERSTACK_EVENTS.EXTERNAL_INVOICING_DASHBOARD_RESET_FILTER, {});
    filters.value = { ...filterDefaults };
  }

  async function generateCsv(customerId: string) {
    toast.loading("Generating CSV");
    const params = getFilterParams();
    const response = await useBapi(
      BAPI_COMMANDS.EXTERNAL_INVOICING_DOWNLOAD_ALL_INVOICES,
      customerId || "",
      selectedPayee.value?.id ?? "",
      params,
    );
    trackerStore.logRudderstackEvent(RUDDERSTACK_EVENTS.EXTERNAL_INVOICING_DASHBOARD_DOWNLOAD_CSV, {
      success: response.success,
      filters: filters.value,
    });
    if (!response.success) {
      toast.error("Failed to generate CSV");
      return;
    }
    const link = document.createElement("a");
    link.href = URL.createObjectURL(response.data);
    link.download = response.data.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.dismiss();
  }

  return {
    selectedPayee,
    total,
    page,
    sort,
    constantList,
    getPayee,
    getInvoicingConstants,
    getInvoice,
    filters,
    filterDefaults,
    getFilterParams,
    resetFilters,
    generateCsv,
  };
});
